import { Component, ErrorInfo, ReactNode } from "react"

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  error?: Error
  hasError: boolean
}

/**
 * Error Boundary for Static Query failures
 * Fixes Sentry issue UAG-WEB-M
 *
 * Wraps components that use useStaticQuery to gracefully handle
 * static query JSON loading failures
 */
class StaticQueryErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { error, hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to Sentry
    console.error(
      "StaticQuery Error Boundary caught an error:",
      error,
      errorInfo
    )

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  render() {
    if (this.state.hasError) {
      // Render custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI
      return null
    }

    return this.props.children
  }
}

export default StaticQueryErrorBoundary
